
import { useState, useCallback, useMemo } from 'react';
import { addWeeks, subWeeks, startOfWeek, addDays } from 'date-fns';
import { useScheduleClasses } from '@/hooks/useScheduleClasses';
import { useSettingsStore } from '@/store/api/settingsStore';
import { DEFAULT_OPEN_DAYS_OF_WEEK } from '@/utils/scheduleUtils';

/**
 * Hook for managing schedule state and navigation
 * Refactored to use useScheduleClasses for data fetching
 *
 * This hook now only handles:
 * - Date selection and navigation (prev/next week)
 * - Week dates calculation
 *
 * Data fetching is delegated to useScheduleClasses
 */
export const useScheduleState = () => {
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [currentMonth, setCurrentMonth] = useState<Date>(new Date());
  const [selectedCalendarId, setSelectedCalendarId] = useState<string | null>(null);
  const viewMode = 'weekly';

  const openDaysOfWeek = useSettingsStore(
    (s) => s.settings.schedule?.openDaysOfWeek ?? DEFAULT_OPEN_DAYS_OF_WEEK
  );

  // Calculate week dates based on selected date, filtered by openDaysOfWeek
  const weekDates = useMemo(() => {
    if (!selectedDate) return [];
    const weekStart = startOfWeek(selectedDate, { weekStartsOn: 1 });
    const allDays = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
    const allowed = openDaysOfWeek.length > 0 ? openDaysOfWeek : DEFAULT_OPEN_DAYS_OF_WEEK;
    return allDays.filter((d) => allowed.includes(d.getDay()));
  }, [selectedDate, openDaysOfWeek]);
  
  // Calculate date range for data fetching
  // ✅ Use getTime() for stable dependencies to prevent unnecessary re-renders
  const dateRange = useMemo(() => {
    if (weekDates.length === 0) return { startDate: undefined, endDate: undefined };
    return {
      startDate: weekDates[0],
      endDate: weekDates[weekDates.length - 1]
    };
  }, [weekDates[0]?.getTime(), weekDates[weekDates.length - 1]?.getTime()]);
  
  // Use the centralized schedule classes hook
  const {
    classes,
    isLoading,
    error,
    fetchClasses,
    refreshClasses
  } = useScheduleClasses({
    startDate: dateRange.startDate,
    endDate: dateRange.endDate,
    calendarId: selectedCalendarId,
    autoFetch: true,
    enablePrefetch: true
  });
  
  // Handle date selection
  const handleDateSelect = useCallback((date: Date) => {
    setSelectedDate(date);
  }, []);
  
  // Handle month change (for calendar)
  const handleMonthChange = useCallback((month: Date) => {
    setCurrentMonth(month);
  }, []);
  
  // Bornes de la semaine effectivement AFFICHÉE (`weekDates` filtre selon les
  // jours d'ouverture). `addDays(weekStart, 5)` codait en dur « lundi→samedi »
  // et excluait le dimanche du chargement alors qu'il pouvait être affiché :
  // après navigation par flèches, la colonne dimanche restait vide.
  const getFetchRangeFor = useCallback((date: Date) => {
    const weekStart = startOfWeek(date, { weekStartsOn: 1 });
    const allowed = openDaysOfWeek.length > 0 ? openDaysOfWeek : DEFAULT_OPEN_DAYS_OF_WEEK;
    const openDays = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i))
      .filter((d) => allowed.includes(d.getDay()));
    return {
      weekStart: openDays[0] ?? weekStart,
      weekEnd: openDays[openDays.length - 1] ?? addDays(weekStart, 6),
    };
  }, [openDaysOfWeek]);

  // Handle navigation - previous week (immediate fetch)
  const handlePrevious = useCallback(() => {
    const newDate = subWeeks(selectedDate, 1);
    setSelectedDate(newDate);

    const { weekStart, weekEnd } = getFetchRangeFor(newDate);

    // Fetch immediately (fetchClasses clears any pending debounce)
    fetchClasses(weekStart, weekEnd);
  }, [selectedDate, fetchClasses, getFetchRangeFor]);
  
  // Handle navigation - next week (immediate fetch)
  const handleNext = useCallback(() => {
    const newDate = addWeeks(selectedDate, 1);
    setSelectedDate(newDate);

    const { weekStart, weekEnd } = getFetchRangeFor(newDate);

    // Fetch immediately (fetchClasses clears any pending debounce)
    fetchClasses(weekStart, weekEnd);
  }, [selectedDate, fetchClasses, getFetchRangeFor]);
  
  return {
    selectedDate,
    currentMonth,
    viewMode,
    weekDates,
    selectedCalendarId,
    setSelectedCalendarId,
    classes,
    isLoading,
    error,
    handleDateSelect,
    handleMonthChange,
    handlePrevious,
    handleNext,
    fetchClasses
  };
};
